import React from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import DisputeManager from './DisputeManager';
import UserManager from './UserManager';
import RevenueOverview from './RevenueOverview';
import PreLaunchChecklist from './PreLaunchChecklist';
import { useDisputedOrders } from '../../hooks/useAdminData';
import OrderList   from './orders/OrderList';
import OrderDetail from './orders/OrderDetail';
import AuditLog from './audit/AuditLog';
import ActivityLog from './activity/ActivityLog';
import SupportCases from './support/SupportCases';
import SupportCaseDetail from './support/SupportCaseDetail';
import FinanceDashboard from './finance/FinanceDashboard';
import AdminSettings from './settings/AdminSettings';
import AdminTerms from './settings/AdminTerms';
import AdminSuppliers from './suppliers/AdminSuppliers';
import AdminMasses from './masses/AdminMasses';
import AdminBroadcasts from './broadcasts/AdminBroadcasts';
import AdminInvoices from './invoices/AdminInvoices';
import AdminCarriers from './carriers/AdminCarriers';
import { useTheme } from '../../contexts/ThemeContext';
import { COLORS } from '../../styles/tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function AdminDashboard() {
  const { orders: disputed } = useDisputedOrders();
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();

  return (
    <div style={styles.root}>
      <Navbar />

      <div style={{ ...styles.body, flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Sidemeny — blir en horisontal, rullbar fanelinje på mobil */}
        <aside style={isMobile ? styles.tabBar : styles.sidebar}>
          {!isMobile && <div style={styles.sidebarTitle}>Admin</div>}

          <NavItem to="/admin/disputes" icon="⚖️" label="Tvister">
            {disputed.length > 0 && (
              <span style={styles.badge}>{disputed.length}</span>
            )}
          </NavItem>
          <NavItem to="/admin/users"     icon="👥" label="Brukere" />
          <NavItem to="/admin/orders"    icon="📦" label="Ordrer" />
          <NavItem to="/admin/audit"     icon="📋" label="Audit log" />
          <NavItem to="/admin/activity"  icon="🔎" label="Aktivitet" />
          <NavItem to="/admin/support"   icon="💬" label="Saker" />
          <NavItem to="/admin/finance"   icon="💰" label="Finans" />
          <NavItem to="/admin/invoices"  icon="🧾" label="Faktura" />
          <NavItem to="/admin/carriers"  icon="🚛" label="Transportører" />
          <NavItem to="/admin/checklist" icon="✅" label="Sjekkliste" />
          <NavItem to="/admin/settings"  icon="⚙️" label="Innstillinger" />
          <NavItem to="/admin/terms"     icon="📜" label="Vilkår" />
          <NavItem to="/admin/suppliers" icon="🏗️" label="Leverandører" />
          <NavItem to="/admin/masser" icon="⛰️" label="Masser" />
          <NavItem to="/admin/broadcasts" icon="📣" label="Meldinger" />
        </aside>

        {/* Innholdsområde — colorScheme gir native kontroller (select, dato, rullefelt) riktig tema */}
        <main style={{ ...styles.main, colorScheme: resolvedTheme }}>
          <Routes>
            <Route path="disputes"  element={<DisputeManager />} />
            <Route path="users"     element={<UserManager />} />
            <Route path="revenue"   element={<Navigate to="/admin/finance" replace />} />
            <Route path="finance"   element={<FinanceDashboard />} />
            <Route path="invoices"  element={<AdminInvoices />} />
            <Route path="carriers"  element={<AdminCarriers />} />
            <Route path="checklist" element={<PreLaunchChecklist />} />
            <Route path="orders"     element={<OrderList />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="audit"     element={<AuditLog />} />
            <Route path="activity"  element={<ActivityLog />} />
            <Route path="support"          element={<SupportCases />} />
            <Route path="support/:caseId"  element={<SupportCaseDetail />} />
            <Route path="settings"  element={<AdminSettings />} />
            <Route path="terms"     element={<AdminTerms />} />
            <Route path="suppliers"   element={<AdminSuppliers />} />
            <Route path="masser"      element={<AdminMasses />} />
            <Route path="broadcasts" element={<AdminBroadcasts />} />
            <Route path="*"         element={<Navigate to="disputes" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, icon, label, children }) {
  const isMobile = useIsMobile();
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...styles.navItem,
        ...(isMobile ? styles.navItemMobile : {}),
        background:  isActive ? '#2d2d4e' : 'transparent',
        color:       isActive ? '#fff' : 'rgba(255,255,255,0.7)',
        fontWeight:  isActive ? 600 : 400,
      })}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {children}
    </NavLink>
  );
}

const styles = {
  root:        { display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' },
  body:        { flex: 1, display: 'flex', overflow: 'hidden' },
  sidebar:     { width: 200, background: COLORS.brandPanel, display: 'flex', flexDirection: 'column', flexShrink: 0, padding: '16px 0' },
  sidebarTitle:{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, padding: '0 20px 16px' },
  navItem:     { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', textDecoration: 'none', fontSize: 14, borderRadius: 6, margin: '0 8px 4px', transition: 'background 0.15s' },
  badge:       { background: '#e63946', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', marginLeft: 4 },
  tabBar:      { display: 'flex', gap: 4, overflowX: 'auto', background: COLORS.brandPanel, flexShrink: 0, padding: '6px 8px' },
  navItemMobile:{ flexShrink: 0, whiteSpace: 'nowrap', padding: '8px 12px', margin: 0, fontSize: 13, gap: 6 },
  main:        { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: COLORS.bg },
};
